import React from 'react'
import './ICSDateBox.css'
import { Controller, Control, FieldValues, FieldErrors } from 'react-hook-form'
import DateBox from 'devextreme-react/date-box'
import { Label, FormFeedback } from 'reactstrap'
import ICSToolTip from '../ICSToolTip/ICSToolTip'
import { useTimezone } from '../../hooks/useTimezone'

/**
 * Datetime picker whose form value is an ISO string (UTC instant). The picker
 * itself works in the browser zone, so the value is shifted through the user's
 * preferred timezone on the way in and out: the wall clock the user sees and
 * types is the preferred zone's wall clock.
 */
const ICSDateBox = ({
  name,
  id = `${name}DateBox`,
  control,
  errors,
  defaultValue = null,
  label = '',
  placeholder = 'Select a value...',
  helpText
}: ICSDateBoxProps) => {
  const { toPickerDate, fromPickerDate } = useTimezone()

  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({
          field: { onChange, value, name, ref },
          fieldState: { invalid, isTouched, isDirty },
        }) => {
          return (
            <>
              <Label for={id} className={label ? 'input-label hidden' : 'input-label'}>
                {label}
                {helpText && (
                  <ICSToolTip
                    id={`${name}ToolTip`}
                    text={helpText}
                    target={`#${name}ToolTip`}
                  />
                )}
              </Label>
              <DateBox
                id={id}
                placeholder={placeholder}
                name={name}
                type='datetime'
                showClearButton={true}
                value={toPickerDate(value) ?? undefined}
                isValid={!invalid}
                displayFormat='yyyy/MM/dd HH:mm:ss'
                onValueChanged={e => {
                  if (e.value === null) {
                    onChange(e.value)
                  } else {
                    onChange(fromPickerDate(new Date(e.value))?.toISOString() ?? null)
                  }
                }}
                className={
                  !(isTouched || isDirty) || value === defaultValue
                    ? ''
                    : value !== defaultValue
                    ? 'custom-valid-datebox'
                    : 'custom-invalid-datebox'
                }
                ref={ref}
                acceptCustomValue={false}
              />
            </>
          )
        }}
      />
      <FormFeedback
        valid={errors?.[name] ? false : true}
        role='alert'
        id={`${name}Error`}
        style={{ display: 'block' }}
      >
        {errors?.[name]?.message}
      </FormFeedback>
    </>
  )
}

export default ICSDateBox

type ICSDateBoxProps<TFieldValues extends FieldValues = FieldValues> = {
  id?: string
  name: string
  control: Control<TFieldValues>
  errors: FieldErrors<TFieldValues>
  defaultValue?: string | null
  label?: string
  placeholder?: string
  helpText?: string
  hideLabel?: boolean
}
